/**
 * CanvasDocument — Full document state
 * Per PLAN.md models/CanvasDocument.js
 */

import { Node } from './Node.js';
import { Connection } from './Connection.js';
import { Group } from './Group.js';
import { eventBus, Events } from '../core/EventBus.js';

export class CanvasDocument {
    /**
     * Create a new canvas document
     * @param {string} name - Document name
     */
    constructor(name = 'Untitled') {
        this.id = crypto.randomUUID();
        this.name = name;
        this.createdAt = Date.now();
        this.modifiedAt = Date.now();

        // Viewport state
        this.viewport = { x: 0, y: 0, zoom: 1.0 };

        // Settings
        this.showGrid = true;
        this.snapToGrid = false;
        this.theme = 'light'; // 'light' | 'dark'

        // Content
        this.nodes = new Map();       // id → Node
        this.connections = new Map(); // id → Connection
        this.groups = new Map();      // id → Group

        // Cached arrays for performance (invalidated on mutation)
        this._nodesCache = null;
        this._connectionsCache = null;
        this._groupsCache = null;
    }

    // ==========================================
    // Node Operations
    // ==========================================

    /**
     * Add a node
     * @param {Node} node 
     */
    addNode(node) {
        this.nodes.set(node.id, node);
        this._nodesCache = null; // Invalidate cache
        this.touch();
        eventBus.emit(Events.NODE_CREATE, node);
        eventBus.emit(Events.RENDER_REQUEST);
    }

    /**
     * Get a node by ID
     * @param {string} id 
     * @returns {Node|undefined}
     */
    getNode(id) {
        return this.nodes.get(id);
    }

    /**
     * Remove a node by ID
     * @param {string} id 
     */
    removeNode(id) {
        const node = this.nodes.get(id);
        if (node) {
            // Remove from group if exists
            if (node.groupId) {
                const group = this.groups.get(node.groupId);
                if (group) {
                    group.removeNode(id);
                    if (group.isEmpty()) {
                        this.groups.delete(group.id);
                    }
                }
            }

            // Remove connected connections
            for (const [connId, conn] of this.connections) {
                if (conn.involvesNode(id)) {
                    this.connections.delete(connId);
                }
            }

            this.nodes.delete(id);
            this._nodesCache = null; // Invalidate cache
            this.touch();
            eventBus.emit(Events.NODE_DELETE, { id });
            eventBus.emit(Events.RENDER_REQUEST);
        }
    }

    /**
     * Get all nodes (cached for performance)
     * @returns {Node[]}
     */
    getAllNodes() {
        if (!this._nodesCache) {
            this._nodesCache = Array.from(this.nodes.values());
        }
        return this._nodesCache;
    }

    /**
     * Get node at position
     * @param {number} x 
     * @param {number} y 
     * @returns {Node|null}
     */
    getNodeAtPosition(x, y) {
        // Iterate in reverse to get topmost node first
        const nodes = this.getAllNodes().reverse();
        for (const node of nodes) {
            if (node.containsPoint(x, y)) {
                return node;
            }
        }
        return null;
    }

    // ==========================================
    // Connection Operations
    // ==========================================

    /**
     * Add a connection
     * @param {Connection} connection 
     */
    addConnection(connection) {
        this.connections.set(connection.id, connection);
        this._connectionsCache = null; // Invalidate cache
        this.touch();
        eventBus.emit(Events.CONNECTION_CREATE, connection);
        eventBus.emit(Events.RENDER_REQUEST);
    }

    /**
     * Get a connection by ID
     * @param {string} id 
     * @returns {Connection|undefined}
     */
    getConnection(id) {
        return this.connections.get(id);
    }

    /**
     * Remove a connection by ID
     * @param {string} id 
     */
    removeConnection(id) {
        this.connections.delete(id);
        this._connectionsCache = null; // Invalidate cache
        this.touch();
        eventBus.emit(Events.CONNECTION_DELETE, { id });
        eventBus.emit(Events.RENDER_REQUEST);
    }

    /**
     * Get all connections (cached for performance)
     * @returns {Connection[]}
     */
    getAllConnections() {
        if (!this._connectionsCache) {
            this._connectionsCache = Array.from(this.connections.values());
        }
        return this._connectionsCache;
    }

    /**
     * Get connections for a node
     * @param {string} nodeId 
     * @returns {Connection[]}
     */
    getConnectionsForNode(nodeId) {
        return this.getAllConnections().filter(c => c.involvesNode(nodeId));
    }

    // ==========================================
    // Group Operations
    // ==========================================

    /**
     * Add a group
     * @param {Group} group 
     */
    addGroup(group) {
        this.groups.set(group.id, group);
        this._groupsCache = null; // Invalidate cache
        this.touch();
        eventBus.emit(Events.GROUP_CREATE, group);
        eventBus.emit(Events.RENDER_REQUEST);
    }

    /**
     * Get a group by ID
     * @param {string} id 
     * @returns {Group|undefined}
     */
    getGroup(id) {
        return this.groups.get(id);
    }

    /**
     * Remove a group by ID
     * @param {string} id 
     */
    removeGroup(id) {
        this.groups.delete(id);
        this._groupsCache = null; // Invalidate cache
        this.touch();
        eventBus.emit(Events.GROUP_DELETE, { id });
        eventBus.emit(Events.RENDER_REQUEST);
    }

    /**
     * Get all groups (cached for performance)
     * @returns {Group[]}
     */
    getAllGroups() {
        if (!this._groupsCache) {
            this._groupsCache = Array.from(this.groups.values());
        }
        return this._groupsCache;
    }

    // ==========================================
    // Settings
    // ==========================================

    /**
     * Toggle grid visibility
     */
    toggleGrid() {
        this.showGrid = !this.showGrid;
        this.touch();
        eventBus.emit(Events.GRID_TOGGLE, { showGrid: this.showGrid });
        eventBus.emit(Events.RENDER_REQUEST);
    }

    /**
     * Toggle snap to grid
     */
    toggleSnap() {
        this.snapToGrid = !this.snapToGrid;
        this.touch();
    }

    /**
     * Set theme
     * @param {string} theme 
     */
    setTheme(theme) {
        this.theme = theme;
        this.touch();
        eventBus.emit(Events.THEME_CHANGE, { theme });
    }

    // ==========================================
    // Utilities
    // ==========================================

    /**
     * Update modified timestamp
     */
    touch() {
        this.modifiedAt = Date.now();
    }

    /**
     * Clear all content
     */
    clear() {
        this.nodes.clear();
        this.connections.clear();
        this.groups.clear();
        // Invalidate all caches
        this._nodesCache = null;
        this._connectionsCache = null;
        this._groupsCache = null;
        this.touch();
        eventBus.emit(Events.RENDER_REQUEST);
    }

    // ==========================================
    // Serialization
    // ==========================================

    /**
     * Serialize to JSON
     * @returns {Object}
     */
    toJSON() {
        return {
            id: this.id,
            name: this.name,
            createdAt: this.createdAt,
            modifiedAt: this.modifiedAt,
            viewport: { ...this.viewport },
            showGrid: this.showGrid,
            snapToGrid: this.snapToGrid,
            theme: this.theme,
            nodes: this.getAllNodes().map(n => n.toJSON()),
            connections: this.getAllConnections().map(c => c.toJSON()),
            groups: this.getAllGroups().map(g => g.toJSON())
        };
    }

    /**
     * Create document from JSON
     * @param {Object} data 
     * @returns {CanvasDocument}
     */
    static fromJSON(data) {
        const doc = new CanvasDocument(data.name);
        doc.id = data.id;
        doc.createdAt = data.createdAt;
        doc.modifiedAt = data.modifiedAt;
        doc.viewport = data.viewport || { x: 0, y: 0, zoom: 1.0 };
        doc.showGrid = data.showGrid ?? true;
        doc.snapToGrid = data.snapToGrid ?? false;
        doc.theme = data.theme || 'light';

        // Restore nodes
        for (const nodeData of data.nodes || []) {
            doc.nodes.set(nodeData.id, Node.fromJSON(nodeData));
        }

        // Restore connections
        for (const connData of data.connections || []) {
            doc.connections.set(connData.id, Connection.fromJSON(connData));
        }

        // Restore groups
        for (const groupData of data.groups || []) {
            doc.groups.set(groupData.id, Group.fromJSON(groupData));
        }

        return doc;
    }
}
